/* eslint-disable consistent-return */
import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import Tooltip from '@mui/material/Tooltip';
import { CircularProgress } from '@mui/material';
import LensBadgeList from '../common/LensBadgeList';
import PreviousStatusChip from '../common/PreviousStatusChip';
import { useGetEntityChildren } from 'api/bitedb/entities/entities.hooks';
import { GlobalContext } from 'providers/GlobalProvider';
import { formatEntityRelationshipTypeLabel } from '../../../utils/biteDbRelationshipTemplates';

interface ISingleResultRow {
  key?: number;
  supplier?: any;
  location?: string;
  selectedItems?: any;
  setSelectedItems?: any;
  screeningHistory?: any;
  onHitCountUpdate?: any;
  handleEdit?: any;
  handleDelete?: any;
  viewType?: string;
  hierarchyLevel?: number;
  hasChildren?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  databaseId?: string;
  relationshipType?: string | null;
  isContextParent?: boolean;
  isSearchMode?: boolean;
  searchMatchedChildren?: any[];
  expandedEntityIds?: Record<string, boolean>;
  onToggleEntityExpand?: (entityId: string) => void;
  aiInProgress?: boolean;
  screeningResultId?: string | null;
  screeningInProgress?: boolean;
  useOutlinedStatusBadge?: boolean;
  screeningFailureWarning?: {
    show: boolean;
    message: string;
    errorCode?: string;
    failedRecordIds?: string[];
  } | null;
}

const Row: React.FC<ISingleResultRow> = ({
  key,
  supplier,
  location,
  selectedItems,
  setSelectedItems,
  screeningHistory,
  handleEdit,
  handleDelete,
  viewType,
  aiInProgress,
  screeningResultId = null,
  screeningInProgress = false,
  useOutlinedStatusBadge = false,
  hierarchyLevel = 0,
  hasChildren = false,
  isExpanded = false,
  onToggleExpand,
  databaseId,
  relationshipType,
  isContextParent = false,
  isSearchMode = false,
  searchMatchedChildren,
  expandedEntityIds,
  onToggleEntityExpand,
  screeningFailureWarning,
}) => {
  const currentSearch =
    typeof window !== 'undefined' ? window.location.search : '';
  const rowFailureNote =
    typeof supplier?.last_run_error_note === 'string' &&
    supplier.last_run_error_note.trim().length > 0
      ? supplier.last_run_error_note.trim()
      : null;
  const warningTooltip =
    rowFailureNote ||
    (screeningFailureWarning?.show
      ? 'This row was impacted by a screening issue.'
      : 'This row was impacted by a recent screening issue.');
  const showImpactedIndicator = supplier?.last_run_impacted_by_error === true;

  const entityId = supplier?.entity_id || supplier?.id;

  const { cur_user } = useContext(GlobalContext);
  const selectedTeamId = cur_user?.selected_team?.id
    ? String(cur_user.selected_team.id)
    : undefined;

  const { data: children, isLoading: isLoadingChildren } = useGetEntityChildren(
    entityId,
    databaseId,
    selectedTeamId,
    isExpanded && hasChildren && !isSearchMode,
  );

  const displayChildren = isSearchMode
    ? (searchMatchedChildren || [])
    : (children || []);

  const handleCheckboxClick = (item: any) => {
    setSelectedItems((prevSelected: any[]) => {
      const safePrevSelected = Array.isArray(prevSelected) ? prevSelected : [];
      const alreadySelected = safePrevSelected.some((i) => i.id === item.id);

      return alreadySelected
        ? safePrevSelected.filter((i) => i.id !== item.id)
        : [...safePrevSelected, item];
    });
  };

  const handleToggleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (onToggleExpand) {
      onToggleExpand();
    }
  };

  const isScreeningPending =
    screeningInProgress &&
    Boolean(screeningResultId) &&
    supplier?.last_screening_result_id !== screeningResultId;

  return (
    <>
      <div
        className={`screening-results-row${
          isScreeningPending ? ' is-screening-pending' : ''
        }`}
        key={key}
        style={
          isContextParent && !isScreeningPending
            ? { opacity: 0.55 }
            : undefined
        }
      >
        <div
          className='screening-results-row-default-cell'
          style={{ maxWidth: showImpactedIndicator ? '68px' : '48px', minWidth: showImpactedIndicator ? '68px' : '48px' }}
        >
          <div
            className='screening-results-row-default-cell-inner'
            onClick={() => handleCheckboxClick(supplier ? supplier : [])}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            {showImpactedIndicator && (
              <Tooltip title={warningTooltip} arrow placement='top'>
                <WarningAmberOutlinedIcon
                  sx={{ fontSize: 16, color: '#f59e0b', flexShrink: 0 }}
                />
              </Tooltip>
            )}
            {selectedItems?.some(
              (item: { id: void }) => item.id === supplier.id,
            ) ? (
              <div className='full-list-filters-cont-row-icon-item'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='12'
                  height='12'
                  viewBox='0 0 12 12'
                  fill='none'
                >
                  <path
                    d='M10 3L4.5 8.5L2 6'
                    stroke='var(--text-primary)'
                    strokeWidth='1.6666'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
            ) : (
              <div className='full-list-filters-cont-row-icon-item-unselected'></div>
            )}
          </div>
        </div>
        <div className='screening-results-row-default-cell'>
          <div className='screening-results-row-default-cell-inner'>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
              }}
            >
              {hasChildren && isExpanded && isLoadingChildren ? (
                <CircularProgress
                  size={14}
                  sx={{
                    color: 'var(--card-text)',
                    marginRight: '6px',
                    flexShrink: 0,
                  }}
                />
              ) : hasChildren ? (
                <button
                  type='button'
                  onClick={handleToggleClick}
                  aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    height: 24,
                    minWidth: 24,
                    border: 'none',
                    padding: 0,
                    marginRight: 4,
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  {isExpanded ? (
                    <svg
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M18 9.5L12 15.5L6 9.5'
                        stroke='var(--action-color)'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  ) : (
                    <svg
                      width='8'
                      height='14'
                      viewBox='0 0 8 14'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M1 1L7 7L1 13'
                        stroke='var(--noaction-color)'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  )}
                </button>
              ) : (
                <div
                  style={{
                    width: 24,
                    height: 24,
                    minWidth: 24,
                    marginRight: 4,
                  }}
                />
              )}
              <Link
                to={{
                  pathname: `${location}/${supplier.entity_id}`,
                  search: currentSearch,
                  state: {
                    objectData: {
                      ...supplier,
                      screening: screeningHistory,
                      type: location?.includes('products')
                        ? 'products'
                        : 'supplier',
                      viewType: viewType,
                    },
                  },
                }}
                style={{ minWidth: 0 }}
              >
                <div className='screening-results-row-link-cell-text'>
                  {supplier.name}
                </div>
              </Link>
              {relationshipType && (
                <span
                  className='screening-badge blues'
                  style={{
                    marginLeft: 6,
                    fontSize: 11,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {formatEntityRelationshipTypeLabel(relationshipType)}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className='screening-results-row-default-cell'>
          <div className='screening-results-row-default-cell-inner'>
            <div className='bdb-status-cell'>
              {useOutlinedStatusBadge ? (
                supplier.status_flag === 'barred' ? (
                  <span className='bdb-nc-badge bdb-nc-badge-error'>
                    Barred
                  </span>
                ) : supplier.status_flag === 'quarantined' ? (
                  <span className='bdb-nc-badge bdb-nc-badge-warning'>
                    Quarantined
                  </span>
                ) : supplier.status_flag === 'cleared' ? (
                  <span className='bdb-nc-badge bdb-nc-badge-success'>
                    Cleared
                  </span>
                ) : supplier.status_flag === 'not_reviewed' ? (
                  <span className='bdb-nc-badge bdb-nc-badge-info'>
                    Not Reviewed
                  </span>
                ) : (
                  <span
                    className='bdb-nc-badge'
                    style={{
                      borderColor: 'var(--bg-secondary)',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    New
                  </span>
                )
              ) : supplier.status_flag === 'barred' ? (
                <span className='screening-badge high'>{'Barred'}</span>
              ) : supplier.status_flag === 'quarantined' ? (
                <span className='screening-badge medium'>{'Quarantined'}</span>
              ) : supplier.status_flag === 'cleared' ? (
                <span className='screening-badge low'>{'Cleared'}</span>
              ) : supplier.status_flag === 'not_reviewed' ? (
                <span className='screening-badge blues'>{'Not Reviewed'}</span>
              ) : (
                <span className='screening-badge none'>{'New'}</span>
              )}
              <PreviousStatusChip
                previousStatusFlag={supplier.previous_status_flag}
                associationId={supplier.association_id}
                databaseId={databaseId}
              />
            </div>
          </div>
        </div>
        <div className='screening-results-row-default-cell'>
          <div className='screening-results-row-default-cell-inner'>
            <div className='screening-results-row-default-cell-text'>
              {supplier.risk ? (
                supplier.risk
              ) : aiInProgress ? (
                <span style={{ color: '#f59e0b', fontSize: '12px' }}>
                  Processing...
                </span>
              ) : (
                '-'
              )}
            </div>
          </div>
        </div>
        <div className='screening-results-row-default-cell'>
          <div
            className='screening-results-row-default-cell-inner'
            style={{ alignItems: 'flex-start', width: '100%' }}
          >
            <LensBadgeList value={supplier?.matched_lenses} />
          </div>
        </div>
        <div
          className='screening-results-row-default-cell'
          style={{ justifyContent: 'center' }}
        >
          <div className='screening-results-row-default-cell-inner'>
            <div className='screening-results-row-default-cell-text'>
              {supplier.hit_count || 0}
            </div>
          </div>
        </div>
        <div
          className='screening-results-row-default-cell'
          style={{ justifyContent: 'center' }}
        >
          <div className='screening-results-row-default-cell-inner'>
            <div
              className='screening-results-row-default-cell-text'
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              {supplier.last_run_impacted_by_error === true ? (
                <span style={{ color: '#f59e0b' }}>!</span>
              ) : supplier.change_since_last_screen === true ? (
                <span style={{ color: '#16a34a' }}>&#10003;</span>
              ) : (
                <span style={{ color: '#6b7280' }}>-</span>
              )}
            </div>
          </div>
        </div>
        <div
          className='screening-results-row-default-cell'
          style={{ maxWidth: '56px', paddingLeft: '0px' }}
        >
          <div className='screening-results-row-default-cell-inner'>
            <EditOutlinedIcon
              onClick={() => handleEdit(supplier ? supplier : [])}
              sx={{ fontSize: 20, color: 'var(--card-text)' }}
            />
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='20'
              height='20'
              viewBox='0 0 20 20'
              fill='none'
              onClick={() => handleDelete(supplier ? supplier : [])}
            >
              <path
                d='M7.5 2.5H12.5M2.5 5H17.5M15.8333 5L15.2489 13.7661C15.1612 15.0813 15.1174 15.7389 14.8333 16.2375C14.5833 16.6765 14.206 17.0294 13.7514 17.2497C13.235 17.5 12.5759 17.5 11.2578 17.5H8.74221C7.42409 17.5 6.76503 17.5 6.24861 17.2497C5.79396 17.0294 5.41674 16.6765 5.16665 16.2375C4.88259 15.7389 4.83875 15.0813 4.75107 13.7661L4.16667 5M8.33333 8.75V12.9167M11.6667 8.75V12.9167'
                stroke='var(--card-text)'
                strokeWidth='1.66667'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </div>
      </div>
      {isExpanded &&
        !isLoadingChildren &&
        Array.isArray(displayChildren) &&
        displayChildren.length > 0 && (
          <div
            style={{
              borderLeft: '1px dotted var(--action-color)',
              width: '100%',
              paddingLeft: '12px',
            }}
          >
            {displayChildren.map((child: any) => {
              const childEntityId = child.entity_id || child.id;
              const childIsExpanded = isSearchMode
                ? Boolean(
                    child?._searchMatchedChildren?.length ||
                      child?._contextChildren?.length,
                  )
                : Boolean(expandedEntityIds?.[childEntityId]);

              return (
	                <Row
	                  key={childEntityId}
	                  supplier={child}
	                  location={location}
	                  viewType={viewType}
                  selectedItems={selectedItems}
                  setSelectedItems={setSelectedItems}
                  handleEdit={handleEdit}
                  handleDelete={handleDelete}
                  hierarchyLevel={(hierarchyLevel ?? 0) + 1}
                  hasChildren={Boolean(child.has_children)}
                  isExpanded={childIsExpanded}
                  onToggleExpand={
                    childEntityId && onToggleEntityExpand
                      ? () => onToggleEntityExpand(childEntityId)
                      : undefined
                  }
                  databaseId={databaseId}
                  relationshipType={child.relationship_type ?? null}
                  isSearchMode={isSearchMode}
	                  searchMatchedChildren={
	                    child?._searchMatchedChildren || child?._contextChildren
	                  }
	                  expandedEntityIds={expandedEntityIds}
	                  onToggleEntityExpand={onToggleEntityExpand}
	                  aiInProgress={aiInProgress}
	                />
	              );
	            })}
          </div>
        )}
    </>
  );
};

export default Row;
